import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import * as moment from 'moment-timezone';
import { NgxSmartModalService } from 'ngx-smart-modal';
import {
  AlertService,
  AudienceService,
  CampaignService,
  FunnelService,
  LoaderService,
  PusherService,
  StepTemplateService,
  StorageService,
  UserService,
} from '../../_services';
import { CrudPagedListComponent } from '../../_directives';
import {
  Pager,
  OfferIntents,
  OfferTypes,
  StepTableStatusLabels,
  StepPopupActions,
  StepTableStatuses,
  User,
  StepsSortConfig,
  TableSortDirection,
  StepSortableColumns,
  StepHeaderDetails,
  RetentionTooltips,
  OfferStages,
  AudiencePopup,
  Campaign,
  NotificationType,
  getOfferIntentDescription,
  ResolveOfferIntents,
} from '../../_models';
import { FirstStep, OfferIntentPriority, PopupData, RetentionOffer, RetentionTable } from './retention';
import { getOfferBuilderSection, OfferBuilderSection } from '../../steps-library/step-util';

@Component({
  moduleId: module.id.toString(),
  selector: 'retention-offers-component',
  templateUrl: './retention-offers.component.html',
  styleUrls: ['./retention-offers.component.scss'],
})
export class RetentionOffersComponent extends CrudPagedListComponent implements OnInit {
  user: User;
  campaign: Campaign;
  tableStatuses = StepTableStatuses;
  tableData: RetentionTable[] = [];
  originalTableData: RetentionTable[] = [...this.tableData];
  offerType: OfferTypes = OfferTypes.Downsell;
  isLoading = false;
  stepPopupActions = StepPopupActions;
  audiencePopup = AudiencePopup;
  offerIntents = OfferIntents;
  stepStatusLabel = StepTableStatusLabels;
  stepHeaderDetails = StepHeaderDetails;
  retentionTooltips = RetentionTooltips;
  currentSort: StepsSortConfig = {
    column: null,
    direction: null,
  };
  isAdminView: boolean = false;
  columnDirections = TableSortDirection;
  sortableColumns = StepSortableColumns;
  popupTitle: string = '';
  popupData: PopupData = {
    name: '',
    offer_id: null,
    segment_funnel_id: null,
    audience_id: null,
    offer_type: OfferTypes.Downsell,
    popupType: null,
    offer_type_options: [],
    offer_intent: null,
  };
  public offerTypeOptions: { value: string; label: string }[] = [];
  excludedIntents = [OfferIntents.Legacy];
  currentSection: OfferBuilderSection = OfferBuilderSection.Retention;

  constructor(
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    protected funnelService: FunnelService,
    protected stepService: StepTemplateService,
    protected campaignService: CampaignService,
    protected userService: UserService,
    protected loader: LoaderService,
    public ngxSmartModalService: NgxSmartModalService,
    protected route: ActivatedRoute,
    protected pusherService: PusherService,
    protected storageService: StorageService,
    protected audienceService: AudienceService
  ) {
    super(router, location, route, funnelService, alertService);
    this.objectName = 'offers';
  }

  ngOnInit() {
    this.loader.show();
    this.campaign = this.campaignService.getCurrentCampaign();
    this.isAdminView = this.route.snapshot.data['isAdminView'];
    this.currentSection = this.route.snapshot.data['section'];

    this.userService.getCurrent().subscribe((user: User) => {
      this.user = user;
    });

    if (this.pusherService.channel) {
      this.pusherService.channel.bind(NotificationType[NotificationType.PATH_PUBLISHING], (data: any) => {
        data = data.payload;
        if (data && data.is_completed) {
          this.fetchOfferData();
        }
      });
    }

    if (this.isAdmin) {
      // Need these options for creating new stage
      this.generateIntentOptions();
    }

    this.fetchOfferData();
  }

  fetchOfferData() {
    const offers$ =
      this.currentSection === OfferBuilderSection.Resolve
        ? this.funnelService.listRetentionOffers(null, ResolveOfferIntents, OfferTypes.Downsell, true, this.user.account.id)
        : this.funnelService.listRetentionOffers(this.isAdmin ? null : this.campaign.id, null, OfferTypes.Downsell);

    offers$.subscribe(
      (response: Pager) => {
        this.loader.hide();
        this.tableData = response.results
          .filter(
            (item: RetentionOffer) =>
              this.canShowIntent(item.offer_intent) && item.enabled && this.matchesViewMode(item.offer_intent)
          )
          .map((item: RetentionOffer) => {
            const lastModifiedMoment = moment(item.updated).tz('EST');

            const lastModified = {
              time: lastModifiedMoment.format('HH:mm:ss z'), // "16:18:10 EST"
              date: lastModifiedMoment.format('DD MMM, YYYY'), // "20 Jan, 2025"
            };

            return {
              id: item.id,
              displayId: item.display_id,
              offerName: OfferStages[item.offer_intent],
              status: item.is_active ? StepTableStatuses.Active : StepTableStatuses.Inactive,
              sessions: item.sessions || '--',
              cancels: item.cancels || '--',
              saves: item.saves || '--',
              segments: item.segments_count,
              type: item.offer_intent,
              offerType: item.offer_type,
              lastModified,
              tooltip: this.getTooltipByIntent(item.offer_intent) || '',
              isActive: false,
              isPublishing: false,
              survey: item.survey || null,
              reactivatePath: item.reactivate_path || null,
              offerIntent: item.offer_intent
            };
          });
        this.sortTable();
        this.originalTableData = this.tableData;
      },
      (error) => {
        this.loader.hide();
        this.handleError(error);
      }
    );
  }

  onPublish(item): void {
    item.isActive = true;
    item.isPublishing = true;

    this.funnelService.publish(item.id).subscribe(
      () => {
        item.isPublishing = false;
        this.alertService.success('Stage changes are being published.');
      },
      (error) => {
        this.loader.hide();
        this.handleError(error);
      }
    );
  }

  sortTable() {
    this.tableData.sort((a, b) => {
      const aSessions = this.getSessionsValue(a.sessions);
      const bSessions = this.getSessionsValue(b.sessions);

      // 1️⃣ Sort by sessions if they exist
      if (aSessions !== bSessions) {
        return bSessions - aSessions; // higher sessions first
      }

      // 2️⃣ Fallback to intent priority
      return OfferIntentPriority[a.type] - OfferIntentPriority[b.type];
    });
  }

  generateIntentOptions(): void {
    const options = Object.keys(OfferStages)
      .filter((intentType) => !this.excludedIntents.includes(parseInt(intentType, 10)))
      .filter((intentType) => this.matchesViewMode(parseInt(intentType, 10)))
      .map((intentType) => ({
        value: intentType,
        label: OfferStages[intentType],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.offerTypeOptions = options;
  }

  openCreateStagePopup(): void {
    this.popupTitle = 'Create Stage';
    this.popupData = {
      ...this.popupData,
      offer_id: null,
      offer_type: this.offerType,
      popupType: StepPopupActions.Create,
      offer_type_options: this.offerTypeOptions,
    };
    this.ngxSmartModalService.getModal('retentionStagePopup').open();
  }

  saveRetentionStage() {
    this.fetchOfferData();
    this.closeStagePopup();
  }

  deleteStage(offerId: number): void {
    this.popupTitle = 'Delete Stage?';
    this.popupData = {
      ...this.popupData,
      offer_id: offerId,
      offer_type: this.offerType,
      popupType: StepPopupActions.Delete,
    };
    this.ngxSmartModalService.getModal('retentionStagePopup').open();
  }

  closeStagePopup() {
    this.ngxSmartModalService.getModal('retentionStagePopup').close();
  }

  openSegmentCrudPopup(offerId: number, offerIntent: number, segments?: number) {
    if (segments) {
      this.navigateToOfferSegments(offerId);
    } else {
      this.popupTitle = 'Create Segment';
      this.popupData = {
        ...this.popupData,
        name: '',
        offer_id: offerId,
        offer_intent: offerIntent,
        popupType: AudiencePopup.Create,
      };
      this.ngxSmartModalService.getModal('segmentCrudPopup').open();
    }
  }

  navigateToConfig(offer) {
    this.router.navigate([offer.survey.funnel, 'intent', offer.type, 'settings'], { relativeTo: this.route });
  }

  saveRetentionConfig() {
    this.closeConfigPopup();
  }

  closeConfigPopup() {
    this.ngxSmartModalService.getModal('retentionConfigPopup').close();
  }

  getSessionsValue(sessions: number | string): number {
    return typeof sessions === 'number' ? sessions : -1;
  }

  saveSegment(event: { name?: string; offer_id?: number; audienceId?: number }) {
    this.closeSegmentCrudPopup();

    if (event && event.audienceId) {
      // Existing audience selected -> create the segment directly, skipping the conditions popup
      this.generateSegmentForExistingAudience(event.audienceId);
      return;
    }

    const { name, offer_id } = event;
    if (name && offer_id) {
      this.openSegmentConditionsPopup(name, offer_id);
    }
  }

  generateSegmentForExistingAudience(audienceId: number) {
    const offerId = this.popupData.offer_id;
    this.loader.show();

    this.audienceService.generateSegment(audienceId, { parent_path: offerId }).subscribe(
      () => {
        this.alertService.success('Added new segment', true);
        this.navigateToOfferSegments(offerId);
      },
      (error) => {
        this.loader.hide();
        this.handleError(error);
      }
    );
  }

  closeSegmentCrudPopup() {
    this.ngxSmartModalService.getModal('segmentCrudPopup').close();
  }

  openSegmentConditionsPopup(audienceName: string, offerId: number) {
    this.popupTitle = 'Define Audience Conditions';
    this.popupData = {
      ...this.popupData,
      name: audienceName,
      offer_id: offerId,
      popupType: AudiencePopup.Create,
    };
    this.ngxSmartModalService.getModal('segmentConditionsPopup').open();
  }

  onSegmentBack() {
    this.ngxSmartModalService.getModal('segmentConditionsPopup').close();

    this.popupTitle = 'Create Segment';
    this.popupData = {
      ...this.popupData,
      popupType: AudiencePopup.Create,
    };
    this.ngxSmartModalService.getModal('segmentCrudPopup').open();
  }

  saveSegmentConditions() {
    this.closeSegmentConditionsPopup();
    this.alertService.success('Added new segment', true);
    this.navigateToOfferSegments(this.popupData.offer_id);
  }

  closeSegmentConditionsPopup() {
    this.ngxSmartModalService.getModal('segmentConditionsPopup').close();
    this.navigateToOfferSegments(this.popupData.offer_id);
  }

  navigateToOfferSegments(offerId: number | string) {
    this.router.navigate(['stage', offerId, 'segment'], { relativeTo: this.route });
  }

  get isResolveSection(): boolean {
    return this.currentSection === OfferBuilderSection.Resolve;
  }

  canShowIntent(intent: OfferIntents): boolean {
    if (this.isResolveSection || (this.isAdminView && ResolveOfferIntents.includes(intent))) {
      // resolve path templates are account-level; show them on the Resolve page and in the admin templates list
      return ResolveOfferIntents.includes(intent);
    }

    if (this.campaign) {
      switch (intent) {
        case OfferIntents.CancelSubscription:
          return this.campaign.portal_settings.min_billing_cycle_for_cancel_subscription > 0;

        case OfferIntents.CancelOrder:
          return this.campaign.portal_settings.min_billing_cycle_for_cancel > 0;

        case OfferIntents.CancelSubscriptionHold:
        case OfferIntents.CancelRebillHold:
          return (
            this.campaign.portal_settings.min_billing_cycle_for_cancel_subscription > 0 &&
            this.campaign.portal_settings.min_billing_cycle_for_cancel > 0
          );

        case OfferIntents.CancelTrial:
          return this.campaign.portal_settings.allow_cancel_subscription_trial;

        case OfferIntents.CancelTrialHold:
          return this.campaign.portal_settings.allow_cancel_trial;

        case OfferIntents.ReturnOrder:
          return this.campaign.portal_settings.allow_return;

        case OfferIntents.ReturnSubscription:
          return this.campaign.portal_settings.allow_return_subscription;

        case OfferIntents.ReturnTrial:
          return this.campaign.portal_settings.allow_return_trial;
      }
    }

    return false;
  }

  matchesViewMode(intent: OfferIntents): boolean {
    let matches = true;
    if (!this.isAdminView) {
      matches = getOfferBuilderSection(intent) === this.currentSection;
    }
    return matches;
  }

  getTooltipByIntent(intent: OfferIntents): string {
    const tooltipMap: Partial<Record<OfferIntents, string>> = {
      [OfferIntents.CancelOrder]: this.retentionTooltips.newOrderOneTime,
      [OfferIntents.CancelSubscriptionHold]: this.retentionTooltips.initialOrderSubscription,
      [OfferIntents.CancelRebillHold]: this.retentionTooltips.rebillOrderSubscription,
      [OfferIntents.CancelTrialHold]: this.retentionTooltips.newOrderTrial,
      [OfferIntents.CancelSubscription]: this.retentionTooltips.activeSubscription,
      [OfferIntents.CancelTrial]: this.retentionTooltips.activeTrial,
      [OfferIntents.ReturnOrder]: getOfferIntentDescription(OfferIntents.ReturnOrder),
      [OfferIntents.ReturnSubscription]: getOfferIntentDescription(OfferIntents.ReturnSubscription),
      [OfferIntents.ReturnTrial]: getOfferIntentDescription(OfferIntents.ReturnTrial),
    };

    return tooltipMap[intent] || '';
  }

  sortByName() {
    if (this.currentSort.column === StepSortableColumns.Name) {
      this.currentSort.direction =
        this.currentSort.direction === TableSortDirection.Asc ? TableSortDirection.Desc : TableSortDirection.Asc;
    } else {
      this.currentSort.column = StepSortableColumns.Name;
      this.currentSort.direction = TableSortDirection.Asc;
    }

    this.tableData.sort((a: any, b: any) => {
      const comparison = a.offerName.localeCompare(b.offerName);
      return this.currentSort.direction === TableSortDirection.Asc ? comparison : -comparison;
    });
  }

  sortByLastModified() {
    if (this.currentSort.column === StepSortableColumns.LastModified) {
      this.currentSort.direction =
        this.currentSort.direction === TableSortDirection.Asc ? TableSortDirection.Desc : TableSortDirection.Asc;
    } else {
      this.currentSort.column = StepSortableColumns.LastModified;
      this.currentSort.direction = TableSortDirection.Asc;
    }

    this.tableData.sort((a: any, b: any) => {
      const dateA = new Date(`${a.lastModified.date} ${a.lastModified.time}`);
      const dateB = new Date(`${b.lastModified.date} ${b.lastModified.time}`);
      return this.currentSort.direction === TableSortDirection.Asc
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    });
  }

  isNameSorted(): boolean {
    return this.currentSort.column === StepSortableColumns.Name;
  }

  get isNameAsc(): boolean {
    return this.isNameSorted() && this.currentSort.direction === TableSortDirection.Asc;
  }

  get isNameDesc(): boolean {
    return this.isNameSorted() && this.currentSort.direction === TableSortDirection.Desc;
  }

  isDateSorted(): boolean {
    return this.currentSort.column === StepSortableColumns.LastModified;
  }

  get isDateAsc(): boolean {
    return this.isDateSorted() && this.currentSort.direction === TableSortDirection.Asc;
  }

  get isDateDesc(): boolean {
    return this.isDateSorted() && this.currentSort.direction === TableSortDirection.Desc;
  }

  get isAdmin(): boolean {
    return this.isAdminView && this.user && this.user.is_staff;
  }

  get headerTitle(): string {
    const sectionDetails = this.stepHeaderDetails[this.currentSection];
    let title = this.stepHeaderDetails.retention.title;
    if (sectionDetails) {
      title = sectionDetails.title;
    }
    return title;
  }

  get headerSubtitle(): string {
    const sectionDetails = this.stepHeaderDetails[this.currentSection];
    let subtitle = this.stepHeaderDetails.retention.subtitle;
    if (sectionDetails) {
      subtitle = sectionDetails.subtitle;
    }
    return subtitle;
  }

  navigateToStage(id: number, offerIntent: OfferIntents, reactivatePath: number) {
    this.storageService.set('reactivatePath', reactivatePath || null);
    this.storageService.set('offerIntent', offerIntent);

    this.router.navigate(['stage', id], { relativeTo: this.route });
  }

  navigateToSurvey(survey: FirstStep, offerIntent: OfferIntents) {
    this.storageService.set('offerIntent', offerIntent);
    this.router.navigate([
      'survey',
      'stage',
      survey.funnel,
      'category',
      OfferTypes.Survey,
      survey.category,
      'step',
      survey.id,
    ]);
  }

  isOfferActive(status: StepTableStatuses): boolean {
    return status === StepTableStatuses.Active;
  }
}
